import React from 'react';
import './Exercise.css';
import './Animate.css';
import { connect } from "react-redux";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import {
    ACTIVITY_TYPE_DELAY,
    ACTIVITY_TYPE_EXERCISE, ACTIVITY_TYPE_FINISH,
    startExercise
} from "../tools/ExerciseUtils";
import { unsubscribeIfCan } from "../tools/rxTools";
import AvatarEyes from "../components/AvatarEyes";
import PauseButton from "../components/PauseButton";
import KeyboardEventHandler from 'react-keyboard-event-handler';
import { strings } from "../languages/localizationStrings";
import audioGuide from "../tools/AudioGuide";
import { getAudioEnabled, getAvatar, setLastWorkout } from "../tools/localStorage";
import { trackExerciseStart, trackExerciseComplete, trackExercisePause, trackExerciseResume, trackExerciseAbandon } from "../tools/analytics";
import ProgressTracking from "../tools/progressTracking";
import wakeLockManager from "../tools/WakeLock";
import IrisMascot from "../components/IrisMascot";
import { getIrisCompletionMessage } from "../tools/irisPersonality";


const winSize = 'winSize';

class Exercise extends React.Component {
    stateSub = new Subject();
    resultDebounce = this.stateSub.pipe(debounceTime(300));
    subscription;
    exerciseSubscription;
    play = true;
    currentExerciseSet;
    lastSpokenDelay = null;
    exerciseStartTime = null;

    constructor(props) {
        super(props);

        this.state = {
            windowSize: props.windowSize,
            play: this.play,
            currentExercise: undefined,
            exercises: props.activities,
            startDelay: props.startDelay
        };
        this.subscription = this.resultDebounce.subscribe(x => {
            if (x.action === winSize) {
                this.setState({ windowSize: this.props.windowSize })
            }
        })
    }

    componentDidMount() {
        // Unlock audio for mobile browsers (user gesture from navigation click)
        audioGuide.unlockAudio();
        // Sync audio enabled state
        audioGuide.setEnabled(getAudioEnabled());
        // Acquire wake lock to keep screen on during exercise
        wakeLockManager.acquire();
        // Track exercise start and record start time
        if (this.props.difficulty) {
            trackExerciseStart(this.props.difficulty);
            this.exerciseStartTime = Date.now();
        }
        this.playNext();
    }

    playNext() {
        unsubscribeIfCan(this.exerciseSubscription);
        if (!this.currentExerciseSet) {
            this.startSet(0);
        } else {
            for (let i = 0; i < this.state.exercises.length; i++) {
                if (i === this.currentSetId + 1) {
                    this.startSet(i);
                    return;
                }
            }
        }
    }

    startSet(setId) {
        this.currentSetId = setId;
        let next = this.state.exercises.length > setId + 1 ? this.state.exercises[setId + 1] : undefined;
        let currentSet = this.state.exercises[setId];

        // Reset audio guide state for new exercise
        audioGuide.resetLastAction();
        this.lastSpokenDelay = null;

        // Announce exercise name for EXERCISE type, or special messages for other types
        if (currentSet.type === ACTIVITY_TYPE_EXERCISE) {
            audioGuide.speakExerciseName(currentSet.name);
        } else if (currentSet.type === ACTIVITY_TYPE_DELAY) {
            // Speak blink or get ready message
            if (currentSet.name === strings.blink) {
                audioGuide.speakBlink();
            } else if (currentSet.name === strings.getReady) {
                audioGuide.speakGetReady();
            }
        } else if (currentSet.type === ACTIVITY_TYPE_FINISH) {
            audioGuide.speakCompletion();
            // Track exercise completion & record progress
            if (this.props.difficulty) {
                if (this.exerciseStartTime) {
                    const durationSeconds = Math.round((Date.now() - this.exerciseStartTime) / 1000);
                    trackExerciseComplete(this.props.difficulty, durationSeconds);
                }
                const updatedProgress = ProgressTracking.recordSession(this.props.difficulty);
                this.setState({ completedProgress: updatedProgress });
            }
        }

        this.setState({
            currentExerciseSet: currentSet,
            nextExerciseSet: next,
            currentExercise: undefined
        });

        this.startExercise(this.state.exercises[setId])
    }

    startExercise(exercise, lastPosition) {
        unsubscribeIfCan(this.exerciseSubscription);
        this.currentExerciseSet = exercise;
        this.setState({
            currentExerciseSet: exercise,
            currentExercise: lastPosition
        });
        this.exerciseSubscription = startExercise(exercise, lastPosition).subscribe((x) => {
            // Play direction tone for exercise movements
            if (exercise.type === ACTIVITY_TYPE_EXERCISE) {
                audioGuide.playDirection(x.exercise);
            }
            // For delay countdowns, play beeps
            else if (exercise.type === ACTIVITY_TYPE_DELAY && x.id !== this.lastSpokenDelay) {
                // Play beep at 5, 3, 2, 1
                if (x.id <= 5 && x.id > 0 && (x.id === 5 || x.id <= 3)) {
                    audioGuide.playCountdownBeep(x.id);
                    this.lastSpokenDelay = x.id;
                }
            }

            this.setState({
                eyeAction: x.exercise,
                currentExercise: x
            });
        },
            (e) => console.error('Error on exercise: ', e),
            () => {
                setTimeout(() => {
                    this.playNext();
                }, 0);
            });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (JSON.stringify(prevState) !== JSON.stringify(this.props)) {
            this.stateSub.next({ action: winSize, data: { windowSize: this.props.windowSize } })
        }
    }

    componentWillUnmount() {
        unsubscribeIfCan(this.subscription);
        unsubscribeIfCan(this.exerciseSubscription);
        // Stop audio when leaving
        audioGuide.cancel();
        // Release wake lock
        wakeLockManager.release();
        // Track abandonment if exercise wasn't completed
        if (this.state.currentExerciseSet?.type !== ACTIVITY_TYPE_FINISH && this.props.difficulty) {
            const progressPercent = Math.round((this.currentSetId / this.state.exercises.length) * 100);
            trackExerciseAbandon(
                this.props.difficulty,
                this.state.currentExerciseSet?.name || 'unknown',
                progressPercent
            );
        }
    }

    onPlayButtonChange = (playButtonState) => {
        this.changePlayState(!playButtonState);
    }

    changePlayState = (play) => {
        if (play !== undefined) {
            this.play = play;
        } else {
            this.play = !this.play;
        }
        this.setState({ play: this.play });
        if (!this.play) {
            unsubscribeIfCan(this.exerciseSubscription);
            // Cancel speech when paused
            audioGuide.cancel();
            // Track pause
            if (this.props.difficulty) {
                trackExercisePause(this.props.difficulty, this.state.currentExerciseSet?.name || 'unknown');
            }
        } else {
            // Track resume
            if (this.props.difficulty) {
                trackExerciseResume(this.props.difficulty);
            }
            let lastPosition = this.state.currentExercise?.id === this.currentExerciseSet?.repeat ? undefined : this.state.currentExercise;
            this.startExercise(this.currentExerciseSet, lastPosition);
        }
    }

    keyPressed = (key) => {
        this.changePlayState();
    }

    skipToNextExercise = () => {
        // Only skip if there's a next exercise
        if (this.state.nextExerciseSet) {
            unsubscribeIfCan(this.exerciseSubscription);
            audioGuide.cancel();
            this.playNext();
        }
    }

    render() {

        let count = this.state.currentExercise?.id === undefined ? 1 : this.state.currentExercise.id + 1;
        let delay = this.state.currentExercise?.id === undefined ? this.state.currentExerciseSet?.repeat : this.state.currentExercise.id;
        let status = '' + count + '/' + this.state.currentExerciseSet?.repeat;
        let displayCount = <div className={"displayCount"}>{this.state.delayCount}</div>;
        let next = this.state.nextExerciseSet ? (
            <div
                className={"displayNext"}
                onClick={this.skipToNextExercise}
                title={strings.skipToNext || "Skip to next exercise"}
            >
                {strings.next + ": "} {this.state.nextExerciseSet.name}
            </div>
        ) : null;

        return (
            <div className={"exerciseContainer"}>
                {this.state.delayCount !== undefined &&
                    displayCount
                }

                {this.state.currentExerciseSet?.type === ACTIVITY_TYPE_DELAY &&
                    <div className={"displayCount"}>{this.state.currentExerciseSet?.name} {delay}</div>
                }

                {this.state.currentExerciseSet?.type === ACTIVITY_TYPE_FINISH &&
                    <div className="finish-celebration">
                        <div className="iris-finish">
                            <IrisMascot
                                size="large"
                                mood="excited"
                                message={getIrisCompletionMessage(this.state.completedProgress)}
                            />
                        </div>
                        <div className="finish-title">{this.state.currentExerciseSet?.name}</div>
                        {this.state.completedProgress && (
                            <div className="finish-stats">
                                <div className="finish-stat">
                                    <span className="finish-stat-icon">🔥</span>
                                    <span className="finish-stat-value">{this.state.completedProgress.currentStreak}</span>
                                    <span className="finish-stat-label">{strings.days} {strings.currentStreak?.toLowerCase?.() || 'streak'}</span>
                                </div>
                                <div className="finish-stat">
                                    <span className="finish-stat-icon">💪</span>
                                    <span className="finish-stat-value">{this.state.completedProgress.totalSessions}</span>
                                    <span className="finish-stat-label">{strings.totalSessions?.toLowerCase?.() || 'sessions'}</span>
                                </div>
                            </div>
                        )}
                        <div className="finish-actions">
                            <a href="/dashboard" className="finish-btn finish-btn-primary">{strings.dashboard}</a>
                            <a href="/welcome" className="finish-btn finish-btn-secondary">{strings.continueTraining || 'Back'}</a>
                        </div>
                    </div>
                }

                {this.state.currentExerciseSet &&
                    this.state.currentExerciseSet?.type === ACTIVITY_TYPE_EXERCISE &&
                    <div className={"statusHolder"}>
                        <div className={"currentStep"}>{this.state.currentExerciseSet?.name}</div>
                        <div className={"currentStep"}>{status}</div>
                    </div>
                }
                {next}

                <div className="exerciseEyesArea">
                    <AvatarEyes
                        avatar={getAvatar()}
                        eyeAction={this.state.eyeAction}
                        size={this.state.windowSize}
                        visualHint={this.state.currentExerciseSet?.visualHint}
                    />
                </div>
                {this.state.currentExerciseSet?.type !== ACTIVITY_TYPE_FINISH &&
                    <PauseButton onStateChage={this.onPlayButtonChange} play={this.state.play} />
                }
                <KeyboardEventHandler
                    handleKeys={['space']}
                    onKeyEvent={this.keyPressed} />
            </div>
        )
    };


}

const mapStateToProps = (state, ownProps) => {
    return {
        windowSize: state.windowSize,
    };
};

export default connect(
    mapStateToProps,
    null
)(Exercise);
